import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildShuffleOrder,
  getNextTrackIndex,
  playlistEqGainsFromSource,
  playlistHasNextTrack,
  trackFromStreamUrl,
} from './audioPlaylistStorage';

export function useUrlAudioPlaylist(streamUrl, title = 'Web stream', { bootstrapTracks, bootstrapMeta } = {}) {
  const [playlistName, setPlaylistName] = useState(bootstrapMeta?.name || 'My playlist');
  const [tracks, setTracks] = useState(() => (Array.isArray(bootstrapTracks) ? bootstrapTracks : []));
  const [currentTrackId, setCurrentTrackId] = useState(() => bootstrapTracks?.[0]?.trackId || '');
  const [shuffle, setShuffle] = useState(Boolean(bootstrapMeta?.shuffle));
  const [repeat, setRepeat] = useState(Boolean(bootstrapMeta?.repeat));
  const [eqGains, setEqGains] = useState(() => playlistEqGainsFromSource(bootstrapMeta));
  const [playOrder, setPlayOrder] = useState([]);
  const [status, setStatus] = useState('');
  const [bootstrapApplied, setBootstrapApplied] = useState(
    () => Array.isArray(bootstrapTracks) && bootstrapTracks.length > 0
  );

  useEffect(() => {
    if (bootstrapApplied) return;
    const url = String(streamUrl || '').trim();
    if (!url) return;
    const track = trackFromStreamUrl(url, title);
    setTracks((prev) => {
      const existing = prev.find((t) => t.source === 'url' && t.url === url);
      if (existing) {
        setCurrentTrackId(existing.trackId);
        return prev;
      }
      setCurrentTrackId(track.trackId);
      return prev.length ? [...prev, track] : [track];
    });
  }, [streamUrl, title, bootstrapApplied]);

  useEffect(() => {
    if (!bootstrapTracks?.length || bootstrapApplied) return;
    setTracks(bootstrapTracks);
    setCurrentTrackId(bootstrapTracks[0]?.trackId || '');
    if (bootstrapMeta?.name) setPlaylistName(bootstrapMeta.name);
    if (typeof bootstrapMeta?.shuffle === 'boolean') setShuffle(bootstrapMeta.shuffle);
    if (typeof bootstrapMeta?.repeat === 'boolean') setRepeat(bootstrapMeta.repeat);
    setEqGains(playlistEqGainsFromSource(bootstrapMeta));
    setBootstrapApplied(true);
  }, [bootstrapTracks, bootstrapMeta, bootstrapApplied]);

  const currentIndex = useMemo(
    () => tracks.findIndex((t) => t.trackId === currentTrackId),
    [tracks, currentTrackId]
  );

  useEffect(() => {
    if (!tracks.length) {
      setPlayOrder([]);
      return;
    }
    const start = currentIndex >= 0 ? currentIndex : 0;
    setPlayOrder(shuffle ? buildShuffleOrder(tracks.length, start) : tracks.map((_, i) => i));
  }, [tracks.length, shuffle]); // eslint-disable-line react-hooks/exhaustive-deps

  const addCurrentTrack = useCallback(() => {
    const url = String(streamUrl || '').trim();
    if (!url) {
      setStatus('Load a stream URL first.');
      return;
    }
    const track = trackFromStreamUrl(url, title);
    setTracks((prev) => {
      const exists = prev.some((t) => t.url === url);
      if (exists) {
        setStatus('Track is already in the playlist.');
        return prev;
      }
      setStatus('Added to playlist.');
      setCurrentTrackId(track.trackId);
      return [...prev, track];
    });
  }, [streamUrl, title]);

  const addUrlTrack = useCallback((url, trackTitle) => {
    const track = trackFromStreamUrl(url, trackTitle);
    setTracks((prev) => {
      if (prev.some((t) => t.url === url)) {
        setStatus('Track is already in the playlist.');
        return prev;
      }
      setCurrentTrackId(track.trackId);
      setStatus('Added to playlist.');
      return [...prev, track];
    });
  }, []);

  const advanceTrack = useCallback(() => {
    if (tracks.length <= 1) return;
    const nextIndex = getNextTrackIndex(tracks.length, currentIndex, playOrder, repeat);
    if (nextIndex == null) return;
    setCurrentTrackId(tracks[nextIndex]?.trackId || '');
  }, [tracks, playOrder, currentIndex, repeat]);

  const hasNextTrackInQueue = useMemo(
    () => playlistHasNextTrack(tracks.length, currentIndex, playOrder, repeat),
    [tracks.length, currentIndex, playOrder, repeat]
  );

  const loadTracks = useCallback((loaded, meta) => {
    setTracks(loaded || []);
    setCurrentTrackId(loaded?.[0]?.trackId || '');
    if (meta?.name) setPlaylistName(meta.name);
    if (typeof meta?.shuffle === 'boolean') setShuffle(meta.shuffle);
    if (typeof meta?.repeat === 'boolean') setRepeat(meta.repeat);
    setEqGains(playlistEqGainsFromSource(meta));
  }, []);

  const currentTrack = useMemo(
    () => tracks.find((t) => t.trackId === currentTrackId) || null,
    [tracks, currentTrackId]
  );

  return {
    playlistName,
    setPlaylistName,
    tracks,
    setTracks,
    currentTrackId,
    setCurrentTrackId,
    shuffle,
    setShuffle,
    repeat,
    setRepeat,
    eqGains,
    setEqGains,
    status,
    setStatus,
    addCurrentTrack,
    addUrlTrack,
    advanceTrack,
    loadTracks,
    currentTrack,
    hasNextTrackInQueue,
  };
}
